import { matchPath } from 'react-router-dom'

const APP_NAME = (import.meta.env.VITE_APP_NAME as string | undefined) ?? 'Kaamco'

export interface PageMetadata {
  title: string
  description: string
}

const PAGE_METADATA: Record<string, PageMetadata> = {
  '/': {
    title: `Kaamco — Multi-Company Workforce OS for Attendance, Payroll & HR`,
    description:
      'Kaamco unifies attendance, shifts, leave, payroll, and staff self-service across multiple companies in one secure RBAC-aware workspace.',
  },
  '/pricing': {
    title: `Pricing — Kaamco Workforce OS`,
    description:
      'Transparent Kaamco pricing with a free tier. Pay for what you use across attendance, payroll, leave, documents, and multi-company RBAC.',
  },
  '/login': {
    title: `Sign in | ${APP_NAME}`,
    description: 'Sign in to Kaamco — staff time tracking and attendance management.',
  },
  '/dashboard': {
    title: `Dashboard | ${APP_NAME}`,
    description: 'View your dashboard, attendance summary, and quick actions.',
  },
  '/companies/setup-wizard': {
    title: `Setup Wizard | ${APP_NAME}`,
    description: 'Import employees and complete company onboarding setup.',
  },
  '/companies/setup-wizard/imports': {
    title: `Import History | ${APP_NAME}`,
    description: 'Review past employee imports and what each one changed.',
  },
  '/attendance': {
    title: `Attendance | ${APP_NAME}`,
    description: 'Track clock-in, clock-out, and daily attendance.',
  },
  '/hr': {
    title: `People & HR | ${APP_NAME}`,
    description:
      'Manage people across your companies: job changes, probation, letters, policies, and cases.',
  },
  '/shifts': {
    title: `Shifts | ${APP_NAME}`,
    description: 'Create shift definitions and assign them to your team from one screen.',
  },
  '/my-shifts': {
    title: `My Shifts | ${APP_NAME}`,
    description: 'All shifts assigned to you, grouped by week.',
  },
  '/my-policies': {
    title: `My Policies | ${APP_NAME}`,
    description:
      'Policies and handbooks that apply to you, and those waiting for your acknowledgement.',
  },
  '/my-hr-cases': {
    title: `My HR Cases | ${APP_NAME}`,
    description: 'HR matters shared with you, and any decision you can appeal.',
  },
  '/my-letters': {
    title: `My Letters | ${APP_NAME}`,
    description: 'Letters HR has issued to you, and those waiting for your signature.',
  },
  '/my-attendance': {
    title: `My Attendance | ${APP_NAME}`,
    description: 'Your attendance records across every company you belong to.',
  },
  '/my-time-corrections': {
    title: `My Time Corrections | ${APP_NAME}`,
    description: 'Time correction requests you submitted across every company you belong to.',
  },
  '/wallboard': {
    title: `WallBoard | ${APP_NAME}`,
    description: 'Live organization-wide session and attendance presence.',
  },
  '/my-leaves': {
    title: `My Leaves | ${APP_NAME}`,
    description: 'Your leave requests across every company you belong to.',
  },
  '/my-bonuses': {
    title: `My Bonuses | ${APP_NAME}`,
    description: 'Bonus entries awarded to you across every company you belong to.',
  },
  '/my-expenses': {
    title: `My Expenses | ${APP_NAME}`,
    description: 'Expense claims you submitted, their approval, and when they are paid back.',
  },
  '/expense-approvals': {
    title: `Expense Approvals | ${APP_NAME}`,
    description: 'Expense claims waiting for your approval.',
  },
  '/my-deposits': {
    title: `My Security Deposit | ${APP_NAME}`,
    description: 'Deductions, refunds, and adjustments against your security deposit.',
  },
  '/my-documents': {
    title: `My Documents | ${APP_NAME}`,
    description: 'Documents uploaded by or for you across every company you belong to.',
  },
  '/leave': {
    title: `Leave | ${APP_NAME}`,
    description: 'Request and manage leave, and view leave balance.',
  },
  '/payroll': {
    title: `Payroll | ${APP_NAME}`,
    description: 'View salary slips and payroll processing.',
  },
  '/users': {
    title: `Users | ${APP_NAME}`,
    description: 'Manage staff, managers, HR, and admin users.',
  },
  '/roles': {
    title: `Roles | ${APP_NAME}`,
    description: 'Configure roles and permissions.',
  },
  '/settings/permissions-matrix': {
    title: `Permissions Matrix | ${APP_NAME}`,
    description: 'Define granular access control for organizational roles.',
  },
  '/reports': {
    title: `Reports | ${APP_NAME}`,
    description: 'Organization-wide compliance reporting across companies.',
  },
  '/notifications': {
    title: `Notifications | ${APP_NAME}`,
    description: 'View and manage system notifications.',
  },
  '/security-ledger': {
    title: `Security deposit | ${APP_NAME}`,
    description: 'Track and manage employee security deposit deductions, adjustments, and refunds.',
  },
  '/settings': {
    title: `Settings | ${APP_NAME}`,
    description: 'Organization and system settings.',
  },
  '/classic': {
    title: `Kaamco — Workforce OS (Classic) | ${APP_NAME}`,
    description: 'The previous Kaamco landing page.',
  },
  '/signup': {
    title: `Create your workspace | ${APP_NAME}`,
    description: 'Create a Kaamco workspace for attendance, shifts, leave, and payroll.',
  },
  '/forgot-password': {
    title: `Reset password | ${APP_NAME}`,
    description: 'Request a link to reset your Kaamco password.',
  },
  '/set-password': {
    title: `Set password | ${APP_NAME}`,
    description: 'Choose a password to finish signing in to Kaamco.',
  },
  '/support-session': {
    title: `Support session | ${APP_NAME}`,
    description: 'Kaamco support access to your workspace.',
  },
  '/companies': {
    title: `Companies | ${APP_NAME}`,
    description: 'Companies in your organization and their setup status.',
  },
  '/companies/setup-wizard/drafts/new': {
    title: `New Company Setup | ${APP_NAME}`,
    description: 'Start a new company setup draft.',
  },
  '/companies/setup-wizard/drafts/:draftId': {
    title: `Company Setup | ${APP_NAME}`,
    description: 'Continue a company setup draft.',
  },
  '/companies/:companyId/setup-wizard': {
    title: `Company Setup | ${APP_NAME}`,
    description: 'Finish setting up this company.',
  },
  '/companies/:companyId': {
    title: `Company | ${APP_NAME}`,
    description: 'Company profile, people, structure, and settings.',
  },
  '/time-corrections': {
    title: `Time Corrections | ${APP_NAME}`,
    description: 'Review and decide time correction requests across companies.',
  },
  '/shifts/upcoming': {
    title: `Upcoming Shifts | ${APP_NAME}`,
    description: 'Shifts coming up across your companies.',
  },
  '/my-letters/:documentId': {
    title: `Letter | ${APP_NAME}`,
    description: 'Read and sign a letter HR issued to you.',
  },
  '/my-policies/:assignmentId': {
    title: `Policy | ${APP_NAME}`,
    description: 'Read and acknowledge a policy that applies to you.',
  },
  '/my-hr-cases/new': {
    title: `Raise a Grievance | ${APP_NAME}`,
    description: 'Raise a grievance with HR.',
  },
  '/my-hr-cases/:caseId': {
    title: `HR Case | ${APP_NAME}`,
    description: 'An HR matter shared with you.',
  },
  '/leaves': {
    title: `Leave Requests | ${APP_NAME}`,
    description: 'Review and decide leave requests across companies.',
  },
  '/penalties': {
    title: `Penalties | ${APP_NAME}`,
    description: 'Penalty records across every company you can read.',
  },
  '/bonuses': {
    title: `Bonuses | ${APP_NAME}`,
    description: 'Bonus records across every company you can read.',
  },
  '/expenses': {
    title: `Expenses | ${APP_NAME}`,
    description: 'Company expenses across every company you can read.',
  },
  '/expense-analytics': {
    title: `Expense Analytics | ${APP_NAME}`,
    description: 'Spending trends across companies and categories.',
  },
  '/documents': {
    title: `Documents | ${APP_NAME}`,
    description: 'Employee and company documents.',
  },
  '/candidates': {
    title: `Candidates | ${APP_NAME}`,
    description: 'Candidates in your hiring pipeline.',
  },
  '/payroll/forecast': {
    title: `Calculate Payroll | ${APP_NAME}`,
    description: 'Calculate payroll for the selected companies and month.',
  },
  '/payroll/history': {
    title: `Paid Payroll History | ${APP_NAME}`,
    description: 'Payroll payments already made, with their slips.',
  },
  '/payroll/history/:snapshotId': {
    title: `Payroll Payment | ${APP_NAME}`,
    description: 'A payroll payment and the employees it paid.',
  },
  '/payroll/forecast/items/:companyId/:employeeId': {
    title: `Payroll Item | ${APP_NAME}`,
    description: 'How pay was calculated for this employee.',
  },
  '/payroll/overview': {
    title: `Payroll Overview | ${APP_NAME}`,
    description: 'Payroll batches across the organization.',
  },
  '/payroll/batches/new': {
    title: `Batch Readiness | ${APP_NAME}`,
    description: 'Check which companies are ready for a payroll batch.',
  },
  '/payroll/batches/:batchId': {
    title: `Payroll Batch | ${APP_NAME}`,
    description: 'Payroll batch status, approvals, and payments.',
  },
  '/payroll/batches/:batchId/items/:employeeId': {
    title: `Payroll Item | ${APP_NAME}`,
    description: 'How pay was calculated for this employee.',
  },
  '/payroll/me': {
    title: `My Payroll | ${APP_NAME}`,
    description: 'Your salary slips and payroll history.',
  },
  '/payroll/me/slips/:batchId': {
    title: `Salary Slip | ${APP_NAME}`,
    description: 'Your salary slip for this pay period.',
  },
  '/companies/:companyId/payroll/runs': {
    title: `Payroll Runs | ${APP_NAME}`,
    description: 'Payroll runs for this company.',
  },
  '/companies/:companyId/payroll/runs/:runId': {
    title: `Payroll Run | ${APP_NAME}`,
    description: 'Payroll run status, items, and approvals.',
  },
  '/companies/:companyId/payroll/runs/:runId/items/:itemId': {
    title: `Payroll Item | ${APP_NAME}`,
    description: 'How pay was calculated for this employee.',
  },
  '/companies/:companyId/payroll/runs/:runId/adjust': {
    title: `Post-Approval Adjustment | ${APP_NAME}`,
    description: 'Adjust an approved payroll run.',
  },
  '/tax-rules': {
    title: `Tax Rules | ${APP_NAME}`,
    description: 'Tax rules and salary slabs used by payroll.',
  },
  '/tax-rules/new': {
    title: `New Tax Rule | ${APP_NAME}`,
    description: 'Create a salary-slab tax rule.',
  },
  '/tax-rules/:id': {
    title: `Tax Rule | ${APP_NAME}`,
    description: 'Tax rule slabs, scope, and effective dates.',
  },
  '/tax-rules/:id/edit': {
    title: `Edit Tax Rule | ${APP_NAME}`,
    description: 'Edit a tax rule; saving creates a new version.',
  },
  '/tax-rules/:id/history': {
    title: `Tax Rule History | ${APP_NAME}`,
    description: 'Every version of this tax rule.',
  },
  '/members': {
    title: `Members | ${APP_NAME}`,
    description: 'Company members across your organization.',
  },
  '/users/new': {
    title: `Create User | ${APP_NAME}`,
    description: 'Invite a new user to your organization.',
  },
  '/roles/new': {
    title: `Create Role | ${APP_NAME}`,
    description: 'Create a role and choose its permissions.',
  },
  '/audit-logs': {
    title: `Audit Logs | ${APP_NAME}`,
    description: 'Who did what, and when, across your organization.',
  },
  '/announcements': {
    title: `Announcements | ${APP_NAME}`,
    description: 'Announcements for your organization.',
  },
  '/organization': {
    title: `Organization | ${APP_NAME}`,
    description: 'Organization profile, members, and settings.',
  },
  '/billing': {
    title: `Plan & Billing | ${APP_NAME}`,
    description: 'Your plan, seats, and invoices.',
  },
  '/billing/return': {
    title: `Billing | ${APP_NAME}`,
    description: 'Confirming your subscription.',
  },
  '/support': {
    title: `Support | ${APP_NAME}`,
    description: 'Support tickets for your organization.',
  },
  '/support/:id': {
    title: `Support Ticket | ${APP_NAME}`,
    description: 'A support ticket and its conversation.',
  },
}

const DEFAULT_METADATA: PageMetadata = {
  title: APP_NAME,
  description: 'Kaamco — Staff time tracking, attendance, leave, and payroll management.',
}

const PATTERN_KEYS = Object.keys(PAGE_METADATA).filter((key) => key.includes(':'))

function normalize(pathname: string): string {
  return pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname
}

/**
 * Metadata for a pathname, or undefined when no entry covers it. Keys are
 * route patterns as in routes/index.tsx: an exact key wins (so `/tax-rules/new`
 * beats `/tax-rules/:id`), then the first `:param` pattern that matches.
 */
export function findPageMetadata(pathname: string): PageMetadata | undefined {
  const normalized = normalize(pathname)
  const exact = PAGE_METADATA[normalized]
  if (exact) return exact
  const key = PATTERN_KEYS.find((pattern) => matchPath({ path: pattern, end: true }, normalized))
  return key ? PAGE_METADATA[key] : undefined
}

/**
 * Returns title and description for a given pathname, falling back to the app
 * defaults for paths no entry covers.
 */
export function getPageMetadata(pathname: string): PageMetadata {
  return findPageMetadata(pathname) ?? DEFAULT_METADATA
}
